import axios from 'axios';
import { captureApiError } from './sentry';
import { buildDigitalPrSearchPayload } from './digitalPrSearch';

export { buildDigitalPrSearchPayload } from './digitalPrSearch';

const resolveApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && String(envUrl).trim()) return String(envUrl).trim();

  // Dev default
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const port = window.location.port;
    if (port === '3000') {
      return `http://${host}:8000`;
    }
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8000';
    }
  }

  // Production default: same origin (expects reverse proxy serving /api)
  return '';
};

export const getApiBaseUrl = () => resolveApiBaseUrl();

const API_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else {
      captureApiError(error);
    }
    return Promise.reject(error);
  }
);

// Helper: create a cancellable request
export const makeCancellable = () => {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    cancel: () => controller.abort(),
  };
};

export const contentWriterAPI = {
  getCorpusStatus: () => api.get('/api/content-writer/corpus-status'),
  validateSheet: (sheetUrl) => api.post('/api/content-writer/validate-sheet', { sheet_url: sheetUrl }),
  getProjects: () => api.get('/api/content-writer/projects'),
  getProject: (id) => api.get(`/api/content-writer/projects/${id}`),
  createProject: (name, sheetUrl) => api.post('/api/content-writer/projects', { name, sheet_url: sheetUrl }),
  updateProject: (id, name) => api.put(`/api/content-writer/projects/${id}`, { name }),
  deleteProject: (id) => api.delete(`/api/content-writer/projects/${id}`),
  startRun: (projectId, idempotencyKey) => api.post(`/api/content-writer/projects/${projectId}/runs`, { idempotency_key: idempotencyKey }),
  getRun: (id) => api.get(`/api/content-writer/runs/${id}`),
  cancelRun: (id) => api.post(`/api/content-writer/runs/${id}/cancel`),
  retryRun: (id) => api.post(`/api/content-writer/runs/${id}/retry`),
  acceptReview: (id) => api.post(`/api/content-writer/runs/${id}/accept-review`),
  downloadArticle: (id) => api.get(`/api/content-writer/articles/${id}/download`, { responseType: 'blob' }),
};

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  signup: (email, password, fullName) =>
    api.post('/api/auth/signup', { email, password, full_name: fullName }),
  confirmEmail: (token) => api.post(`/api/auth/confirm-email?token=${encodeURIComponent(token)}`),
  resendConfirmation: (email) => api.post(`/api/auth/resend-confirmation?email=${encodeURIComponent(email)}`),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/api/auth/reset-password', { token, new_password: newPassword }),
  register: (email, password, fullName) =>
    api.post('/api/auth/register', { email, password, full_name: fullName }),
  getMe: (signal) => api.get('/api/auth/me', { signal }),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return Promise.resolve();
  },
};

// Projects API
export const projectsAPI = {
  getAll: (params = {}) => api.get('/api/projects/', { params }),
  getOne: (id) => api.get(`/api/projects/${id}`),
  create: (name, title, description) => 
    api.post('/api/projects/', { name, title, description }),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  delete: (id) => api.delete(`/api/projects/${id}`),
  uploadFile: (projectId, file, stateColumn, valueColumn, sheetName) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('state_column', stateColumn);
    formData.append('value_column', valueColumn);
    if (sheetName) {
      formData.append('sheet_name', sheetName);
    }
    return api.post(`/api/projects/${projectId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importGoogleSheet: (projectId, sheetUrl, stateColumn, valueColumn, sheetName) => {
    const formData = new FormData();
    formData.append('sheet_url', sheetUrl);
    formData.append('state_column', stateColumn);
    formData.append('value_column', valueColumn);
    if (sheetName) {
      formData.append('sheet_name', sheetName);
    }
    return api.post(`/api/projects/${projectId}/google-sheet`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewGoogleSheet: (sheetUrl, sheetName) => {
    const formData = new FormData();
    formData.append('sheet_url', sheetUrl);
    if (sheetName) {
      formData.append('sheet_name', sheetName);
    }
    return api.post('/api/projects/preview-google-sheet', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewFile: (file, sheetName) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sheetName) {
      formData.append('sheet_name', sheetName);
    }
    return api.post('/api/projects/preview-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateMapData: (projectId, dataId, data) =>
    api.put(`/api/projects/${projectId}/map-data/${dataId}`, data),
  deleteMapData: (projectId, dataId) =>
    api.delete(`/api/projects/${projectId}/map-data/${dataId}`),
  getEmbedCode: (projectId) => api.get(`/api/projects/${projectId}/embed-code`),
  regenerateEmbedToken: (projectId) => api.post(`/api/projects/${projectId}/regenerate-embed-token`),
};

// Users API (Admin only)
export const usersAPI = {
  getAll: (signal) => api.get('/api/users/', { signal }),
  create: (email, password, fullName) =>
    api.post('/api/users/', { email, password, full_name: fullName }),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  confirmEmail: (id) => api.post(`/api/users/${id}/confirm-email`),
  resendConfirmation: (id) => api.post(`/api/users/${id}/resend-confirmation`),
  delete: (id) => api.delete(`/api/users/${id}`),
  suspend: (id) => api.post(`/api/users/${id}/suspend`),
  activate: (id) => api.post(`/api/users/${id}/activate`),
  getActivityLogs: (params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.skip) qs.set('skip', params.skip);
    if (params.limit) qs.set('limit', params.limit);
    if (params.action) qs.set('action', params.action);
    if (params.user_id) qs.set('user_id', params.user_id);
    return api.get(`/api/users/activity-logs?${qs.toString()}`, { signal });
  },
  getUsage: (signal) => api.get('/api/users/usage', { signal }),
  getUserUsage: (userId, params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', params.limit);
    const query = qs.toString();
    return api.get(`/api/users/usage/${userId}${query ? `?${query}` : ''}`, { signal });
  },
  getAllProjects: (params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.skip) qs.set('skip', params.skip);
    if (params.limit) qs.set('limit', params.limit);
    if (params.user_id) qs.set('user_id', params.user_id);
    return api.get(`/api/users/all-projects?${qs.toString()}`, { signal });
  },
  getSiteSettings: () => api.get('/api/users/site-settings'),
  updateSiteSettings: (data) => api.put('/api/users/site-settings', data),
  testSmtp: (email) => api.post('/api/users/site-settings/test-email', { email }),
  importCodex: () => api.post('/api/users/site-settings/codex/import'),
  validateCodex: () => api.post('/api/users/site-settings/codex/validate'),
  disconnectCodex: () => api.post('/api/users/site-settings/codex/disconnect'),
  validateCodexAccount: (accountId) => api.post(`/api/users/site-settings/codex/accounts/${accountId}/validate`),
  updateCodexAccount: (accountId, enabled) =>
    api.patch(`/api/users/site-settings/codex/accounts/${accountId}`, { enabled }),
  deleteCodexAccount: (accountId) => api.delete(`/api/users/site-settings/codex/accounts/${accountId}`),
  startCodexLogin: () => api.post('/api/users/site-settings/codex/login/start'),
  getCodexLoginStatus: (sessionId) =>
    api.get(`/api/users/site-settings/codex/login/status?session_id=${encodeURIComponent(sessionId)}`),
  getWatermark: () => api.get('/api/users/watermark'),
};

// Proxy Pool API (admin only)
export const proxiesAPI = {
  list: (params = {}, signal) => {
    const qs = new URLSearchParams();
    qs.set('skip', params.skip || 0);
    qs.set('limit', params.limit || 20);
    return api.get(`/api/proxies/?${qs.toString()}`, { signal });
  },
  addBulk: (proxies) => api.post('/api/proxies/bulk', { proxies }),
  checkAll: () => api.post('/api/proxies/check-all'),
  checkSelected: (ids) => api.post('/api/proxies/bulk-check', { ids }),
  deleteSelected: (ids) => api.delete('/api/proxies/bulk', { data: { ids } }),
  checkOne: (id) => api.post(`/api/proxies/${id}/check`),
  toggle: (id, isEnabled) => api.patch(`/api/proxies/${id}`, { is_enabled: isEnabled }),
  delete: (id) => api.delete(`/api/proxies/${id}`),
};

// Digital PR API
export const digitalPrAPI = {
  listLocations: (signal) => api.get('/api/digital-pr/locations', { signal }),
  listRegions: (countryCode, signal) =>
    api.get(`/api/digital-pr/locations/${countryCode}/regions`, { signal }),
  listCities: (countryCode, params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.region_id) qs.set('region_id', params.region_id);
    if (params.q) qs.set('q', params.q);
    qs.set('limit', params.limit || 100);
    return api.get(`/api/digital-pr/locations/${countryCode}/cities?${qs.toString()}`, { signal });
  },
  listAreas: (countryCode, params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.region_id) qs.set('region_id', params.region_id);
    if (params.q) qs.set('q', params.q);
    qs.set('limit', params.limit || 100);
    return api.get(`/api/digital-pr/locations/${countryCode}/areas?${qs.toString()}`, { signal });
  },
  listProjects: (signal) => api.get('/api/digital-pr/projects', { signal }),
  createProject: (name) => api.post('/api/digital-pr/projects', { name }),
  renameProject: (id, name) => api.put(`/api/digital-pr/projects/${id}`, { name }),
  deleteProject: (id) => api.delete(`/api/digital-pr/projects/${id}`),
  listSearches: (projectId, signal) => api.get(`/api/digital-pr/projects/${projectId}/searches`, { signal }),
  createSearch: (projectId, keyword, selection = {}) =>
    api.post(
      `/api/digital-pr/projects/${projectId}/searches`,
      buildDigitalPrSearchPayload(keyword, selection)
    ),
  getSearch: (searchId, signal) => api.get(`/api/digital-pr/searches/${searchId}`, { signal }),
  saveMatchArticle: (searchId, { articleText, file }) => {
    const formData = new FormData();
    if (articleText) formData.append('article_text', articleText);
    if (file) formData.append('file', file);
    return api.put(`/api/digital-pr/searches/${searchId}/match-article`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeMatchArticle: (searchId) => api.delete(`/api/digital-pr/searches/${searchId}/match-article`),
  pauseSearch: (searchId) => api.post(`/api/digital-pr/searches/${searchId}/pause`),
  resumeSearch: (searchId) => api.post(`/api/digital-pr/searches/${searchId}/resume`),
  cancelSearch: (searchId) => api.post(`/api/digital-pr/searches/${searchId}/cancel`),
  retrySearch: (searchId) => api.post(`/api/digital-pr/searches/${searchId}/retry`),
  listArticles: (projectId, searchId, signal) => {
    const qs = new URLSearchParams();
    if (searchId) qs.set('search_id', searchId);
    qs.set('limit', 1000);
    return api.get(`/api/digital-pr/projects/${projectId}/articles?${qs.toString()}`, { signal });
  },
  retryArticle: (articleId) => api.post(`/api/digital-pr/articles/${articleId}/retry`),
};

// Backlink Monitor API
export const backlinkMonitorAPI = {
  listProjects: (signal) => api.get('/api/backlinks/projects', { signal }),
  createProject: (data, targetUrl) => {
    const payload = typeof data === 'object' ? data : { name: data, target_url: targetUrl };
    return api.post('/api/backlinks/projects', payload);
  },
  updateProject: (id, data) => api.put(`/api/backlinks/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/api/backlinks/projects/${id}`),
  startProject: (id) => api.post(`/api/backlinks/projects/${id}/start`),
  pauseProject: (id) => api.post(`/api/backlinks/projects/${id}/pause`),
  resumeProject: (id) => api.post(`/api/backlinks/projects/${id}/resume`),
  stopProject: (id) => api.post(`/api/backlinks/projects/${id}/stop`),
  listUrls: (projectId, params = {}, signal) => api.get(`/api/backlinks/projects/${projectId}/urls`, { params, signal }),
  addUrls: (projectId, data) => {
    const payload = typeof data === 'object' ? data : { urls: data };
    return api.post(`/api/backlinks/projects/${projectId}/urls`, payload);
  },
  checkProject: (projectId) => api.post(`/api/backlinks/projects/${projectId}/check`),
  checkUrl: (urlId) => api.post(`/api/backlinks/urls/${urlId}/check`),
  updateUrl: (urlId, data) => api.put(`/api/backlinks/urls/${urlId}`, data),
  bulkAction: (projectId, data) => api.post(`/api/backlinks/projects/${projectId}/urls/bulk-action`, data),
  deleteUrl: (urlId) => api.delete(`/api/backlinks/urls/${urlId}`),
};

// Embed API (public, no auth)
export const embedAPI = {
  getData: (token) => api.get(`/api/embed/${token}`),
};

// Support API (auth required)
export const supportAPI = {
  listTickets: (params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    if (params.skip) qs.set('skip', params.skip);
    if (params.limit) qs.set('limit', params.limit);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/api/support/tickets${suffix}`, { signal });
  },
  createTicket: ({ subject, category, message }) =>
    api.post('/api/support/tickets', { subject, category, message }),
  getMessages: (ticketId, signal) =>
    api.get(`/api/support/tickets/${ticketId}/messages`, { signal }),
  sendMessage: (ticketId, body) =>
    api.post(`/api/support/tickets/${ticketId}/messages`, { body }),
  closeTicket: (ticketId) => api.post(`/api/support/tickets/${ticketId}/close`),
  reopenTicket: (ticketId) => api.post(`/api/support/tickets/${ticketId}/reopen`),
  updateTicket: (ticketId, data) => api.patch(`/api/support/tickets/${ticketId}`, data),
};

export default api;
